'use client';
import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

const products = [
  'Ride Hailing Platform',
  'Logistics Platform',
  'Delivery Services',
  'Not sure — help me choose',
];

interface FormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  product: string;
  message: string;
}

function validate(form: FormData): string | null {
  if (!form.name || form.name.trim().length < 2) return 'Full name must be at least 2 characters.';
  if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Please enter a valid email address.';
  if (form.phone && !/^[+]?[\d\s\-()]{7,15}$/.test(form.phone)) return 'Please enter a valid phone number.';
  if (!form.company || form.company.trim().length < 2) return 'Company name is required.';
  if (!form.product) return 'Please select a product of interest.';
  return null;
}

export default function DemoBookingContent() {
  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    product: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.05, rootMargin: '40px 0px -20px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [submitted]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/demo-booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Something went wrong. Please try again.');
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="pt-24 min-h-screen bg-[#EFF6FF]">
      {/* Hero - Gradient Background */}
      <div className="bg-gradient-to-br from-[#0066FF] via-[#3B82F6] to-[#0066FF] py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-on-scroll stagger-1">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
              Book a Demo
            </span>
            <h1 className="font-display text-5xl md:text-6xl font-semibold text-white tracking-tight leading-tight mb-6 mt-2">
              See Movio{' '}
              <span className="italic font-light text-white/80">in action.</span>
            </h1>
            <p className="text-white/80 text-xl max-w-xl mx-auto leading-relaxed animate-on-scroll stagger-2">
              Schedule a personalised demo with our product experts and discover how we can transform your operations.
            </p>
          </div>
        </div>
      </div>

      {/* Form Section - EFF6FF Background */}
      <div className="bg-[#EFF6FF] py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            {/* Left — Why Demo */}
            <div className="lg:col-span-2 flex flex-col gap-8">
              <div>
                <h2 className="font-display text-3xl font-semibold text-[#0066FF] mb-3">
                  What to expect
                </h2>
                <p className="text-[#475569] leading-relaxed">
                  A 30-minute live walkthrough tailored to your business, followed by a Q&A with our team.
                </p>
              </div>
              <ul className="flex flex-col gap-5">
                {[
                  { icon: 'PlayCircleIcon', title: 'Live product walkthrough', desc: 'See the platform in real-time with your use case.' },
                  { icon: 'ChatBubbleLeftRightIcon', title: 'Expert Q&A', desc: 'Ask anything — pricing, integrations, timelines.' },
                  { icon: 'DocumentTextIcon', title: 'Custom proposal', desc: 'Receive a tailored plan within 24 hours.' },
                  { icon: 'ShieldCheckIcon', title: 'No commitment', desc: 'Zero pressure. Just a conversation.' },
                ].map((item) => (
                  <li key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#0066FF]/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#0066FF]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">{item.title}</p>
                      <p className="text-sm text-[#64748B] mt-0.5">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — Form - White Card */}
            <div className="lg:col-span-3 bg-white rounded-2xl border border-[#0066FF]/10 shadow-xl shadow-[#0066FF]/10 p-8 md:p-10">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-[#0066FF]/10 flex items-center justify-center mx-auto mb-6">
                    <Icon name="CheckCircleIcon" size={36} className="text-[#0066FF]" />
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-[#0F172A] mb-3">
                    Demo Booked!
                  </h3>
                  <p className="text-[#475569] max-w-sm mx-auto mb-6">
                    Thanks, <strong>{form.name}</strong>! We've received your request and will confirm your slot at <strong>{form.email}</strong> within a few hours.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: '', email: '', company: '', phone: '', product: '', message: '' });
                    }}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-[#0066FF]/20 text-sm font-medium text-[#0066FF] hover:border-[#0066FF] hover:bg-[#0066FF]/5 transition"
                  >
                    <Icon name="ArrowLeftIcon" size={16} />
                    Back to Form
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <h3 className="font-display text-2xl font-semibold text-[#0F172A] mb-1">
                    Book your demo
                  </h3>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-[#475569]">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Jane Smith"
                        className="px-4 py-3 rounded-xl border border-[#0066FF]/20 text-sm text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent transition bg-white"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-[#475569]">Work Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="jane@company.com"
                        className="px-4 py-3 rounded-xl border border-[#0066FF]/20 text-sm text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent transition bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-[#475569]">Company *</label>
                      <input
                        type="text"
                        name="company"
                        required
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Acme Logistics"
                        className="px-4 py-3 rounded-xl border border-[#0066FF]/20 text-sm text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent transition bg-white"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-[#475569]">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                        className="px-4 py-3 rounded-xl border border-[#0066FF]/20 text-sm text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent transition bg-white"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-[#475569]">Product of Interest *</label>
                    <select
                      name="product"
                      required
                      value={form.product}
                      onChange={handleChange}
                      className="px-4 py-3 rounded-xl border border-[#0066FF]/20 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent transition bg-white"
                    >
                      <option value="" disabled>Select a platform…</option>
                      {products.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-[#475569]">Anything else?</label>
                    <textarea
                      name="message"
                      rows={3}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about your fleet size, current challenges, or specific features you'd like to see…"
                      className="px-4 py-3 rounded-xl border border-[#0066FF]/20 text-sm text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent transition resize-none bg-white"
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold text-white bg-[#0066FF] hover:bg-[#3B82F6] shadow-lg shadow-[#0066FF]/30 hover:shadow-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Sending…' : 'Confirm Demo Booking'}
                    {!loading && <Icon name="ArrowRightIcon" size={18} />}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA Section */}
      <div className="bg-[#0066FF]/5 py-12 border-t border-[#0066FF]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#475569] text-sm">
            Have questions? <span className="text-[#0066FF] font-semibold">Contact our team</span> or check out our <span className="text-[#0066FF] font-semibold">FAQ page</span>
          </p>
        </div>
      </div>
    </section>
  );
}